import { PageHero, LocalBusinessSchema } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { Shield, Target, Users, Clock, MapPin } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function NewcastlePage() {
  return (
    <>
      <LocalBusinessSchema city="Newcastle" latitude={54.9783} longitude={-1.6178} />
      <PageHero
        title="Fractional CISO Services in Newcastle"
        subtitle="Cybersecurity leadership for the North East's digital transformation"
        eyebrow="Newcastle"
        seoTitle="Fractional CISO Newcastle | The CISO Network"
        seoDescription="Expert fractional CISO services for Newcastle businesses. Access senior cybersecurity leadership for the North East's growing tech sector."
      />

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Securing the North East's Digital Renaissance
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Newcastle is experiencing a remarkable digital transformation, evolving from its
                  industrial heritage into one of the UK's most exciting emerging tech hubs. The
                  city's vibrant Quayside and Ouseburn Valley have become centres for digital
                  innovation, while significant government investment in the region has created a
                  thriving ecosystem spanning fintech, healthtech, and creative industries.
                </p>
                <p>
                  North East businesses face evolving cybersecurity challenges. The region's growing
                  fintech sector competes on a national stage and requires robust security
                  programmes. Healthtech innovators working with NHS trusts must navigate complex
                  data governance requirements. Meanwhile, the city's digital agencies and
                  professional services firms handle sensitive client data that demands protection.
                </p>
                <p>
                  Many Newcastle businesses are ambitious scale-ups or established SMEs serving
                  UK-wide clients. These organisations need demonstrable security leadership to win
                  contracts and build trust in competitive markets, but accessing senior CISO talent
                  in the North East can be challenging. Our fractional CISO service provides
                  Newcastle businesses with the strategic security expertise they need to grow
                  confidently.
                </p>
                <p>
                  Newcastle and the North East combine a strong industrial heritage with a rapidly
                  growing technology sector centred around the Helix and the Protocol. The region's
                  businesses — from energy companies to digital startups — face the same cyber
                  threats as London firms but with fewer local security resources. A fractional CISO
                  bridges this gap, providing enterprise-grade expertise within regional budget
                  realities.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Your Account Manager
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Your account manager understands the unique dynamics of the North East
                      business community and its digital ambitions.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Clock className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      North East Coverage
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Supporting businesses across Newcastle, Gateshead, Sunderland, and throughout
                      the North East.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Newcastle Businesses Choose Fractional CISOs
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Strategic security leadership for the North East's digital economy
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Scale-Up Security',
                description:
                  'Security programmes that satisfy investors and enterprise clients alike.',
              },
              {
                icon: Target,
                title: 'Cost Effective',
                description:
                  'Access senior CISO expertise without the overhead of a full-time hire.',
              },
              {
                icon: Users,
                title: 'Regional Understanding',
                description: 'CISOs who understand North East industries and business culture.',
              },
              {
                icon: Clock,
                title: 'Growth Aligned',
                description:
                  'Flexible engagement that scales with your Newcastle business ambitions.',
              },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <benefit.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO Services in Newcastle - FAQs"
        subtitle="Common questions from Newcastle and North East businesses"
        items={[
          {
            id: 'newcastle-1',
            question: 'How does a fractional CISO work with businesses in Newcastle?',
            answer:
              'Our fractional CISOs work with organisations across the UK, including Newcastle. They can attend meetings on-site or work remotely, depending on what suits your business. We match you with a CISO who understands the security challenges facing technology, energy, and health companies.',
          },
          {
            id: 'newcastle-2',
            question: 'What size of business benefits from a fractional CISO in Newcastle?',
            answer:
              'Fractional CISO services are ideal for growing businesses that need strategic security leadership but are not yet ready for a full-time executive hire. This includes Newcastle startups, scale-ups, and mid-sized companies that are winning larger contracts or facing regulatory requirements.',
          },
          {
            id: 'newcastle-3',
            question: 'What industries do your CISOs cover in Newcastle?',
            answer:
              "Our network includes CISOs with experience in technology, energy, and health. We match you with someone who understands your sector's specific regulatory environment and threat landscape, whether you are navigating FCA requirements, NHS Digital standards, or customer security questionnaires.",
          },
          {
            id: 'newcastle-4',
            question: 'What is the first thing a fractional CISO does when they start?',
            answer:
              'Every engagement begins with discovery. Your CISO assesses your current security posture, understands your business priorities, and develops a tailored roadmap. This typically delivers actionable insights within the first 30 days, with quick wins identified early.',
          },
          {
            id: 'newcastle-5',
            question: 'Can a fractional CISO help with board reporting and governance?',
            answer:
              'Yes. A core part of the fractional CISO role is translating technical security risks into business language for boards and leadership teams. Our CISOs build reporting frameworks, present to boards, and establish governance structures that satisfy regulators and investors.',
          },
        ]}
      />

      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Secure Your Newcastle Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Speak with our team today and discover how a fractional CISO can protect your digital
            growth.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
